'use client';

import React from 'react';

/* Fires once when the element first reaches the viewport. Everything on the
   page that animates hangs off this, so the whole site shares one trigger. */
export function useInView(options) {
  const ref = React.useRef(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { setInView(true); io.disconnect(); }
        });
      },
      { threshold: 0.25, ...(options || {}) }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [options]);

  return [ref, inView];
}

export function Reveal({ children, delay = 0, as: Tag = 'div', className = '', ...rest }) {
  const [ref, inView] = useInView();
  return (
    <Tag
      ref={ref}
      className={`rv ${inView ? 'in' : ''} ${className}`.trim()}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* Counts to a value the first time it is seen — used for the funnel numbers,
   which should feel measured rather than decorative. */
export function Count({ to, duration = 1100, suffix = '' }) {
  const [ref, inView] = useInView();
  const [n, setN] = React.useState(0);

  React.useEffect(() => {
    if (!inView) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setN(to); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(to * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return <span ref={ref}>{n}{suffix}</span>;
}
